import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const INTERACTIVE_SELECTOR = 'a, button, input, textarea, select, [role="button"], [data-cursor="interactive"]';
const MEDIA_SELECTOR = 'iframe, video';

const CustomCursor = () => {
    const [isEnabled, setIsEnabled] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const lastState = useRef({ hovered: false, hidden: false });
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const ringX = useSpring(mouseX, { stiffness: 700, damping: 42, mass: 0.18 });
    const ringY = useSpring(mouseY, { stiffness: 700, damping: 42, mass: 0.18 });

    useEffect(() => {
        const mediaQuery = window.matchMedia('(pointer: fine) and (prefers-reduced-motion: no-preference)');
        const update = () => setIsEnabled(mediaQuery.matches);
        update();
        mediaQuery.addEventListener('change', update);
        return () => mediaQuery.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        if (!isEnabled) return undefined;

        const onPointerMove = (event) => {
            mouseX.set(event.clientX);
            mouseY.set(event.clientY);
            setIsVisible(true);

            const target = event.target instanceof Element ? event.target : null;
            const hidden = Boolean(target?.closest(MEDIA_SELECTOR));
            const hovered = !hidden && Boolean(target?.closest(INTERACTIVE_SELECTOR));
            if (hidden !== lastState.current.hidden) {
                lastState.current.hidden = hidden;
                setIsHidden(hidden);
            }
            if (hovered !== lastState.current.hovered) {
                lastState.current.hovered = hovered;
                setIsHovered(hovered);
            }
        };

        const onPointerLeave = () => setIsVisible(false);
        const onPointerEnter = () => setIsVisible(true);
        window.addEventListener('pointermove', onPointerMove, { passive: true });
        document.addEventListener('pointerleave', onPointerLeave);
        document.addEventListener('pointerenter', onPointerEnter);
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerleave', onPointerLeave);
            document.removeEventListener('pointerenter', onPointerEnter);
        };
    }, [isEnabled, mouseX, mouseY]);

    if (!isEnabled || !isVisible) return null;

    return (
        <motion.div
            aria-hidden="true"
            className="pointer-events-none z-[100000] fixed inset-0"
            animate={{ opacity: isHidden ? 0 : 1 }}
            transition={{ duration: 0.15 }}
        >
            <motion.div
                className="absolute top-0 left-0 w-10 h-10 border border-cinema-gold/50 rounded-full"
                style={{ x: ringX, y: ringY, translateX: '-50%', translateY: '-50%' }}
                animate={{
                    scale: isHovered ? 1.45 : 1,
                    backgroundColor: isHovered ? 'rgba(212, 175, 55, 0.12)' : 'rgba(212, 175, 55, 0)',
                }}
            />
            <motion.div
                className="absolute top-0 left-0 w-1.5 h-1.5 bg-cinema-gold rounded-full shadow-[0_0_10px_#d4af37]"
                style={{ x: mouseX, y: mouseY, translateX: '-50%', translateY: '-50%' }}
                animate={{ scale: isHovered ? 0.4 : 1 }}
            />
        </motion.div>
    );
};

export default CustomCursor;
